import React from "react";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import { CustomPlaceholderValue } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { customFieldToInputData, ICustomFieldDefinition, serializeCustomFieldValue } from "../../src/taskpane/components/helpers/customFieldsHelper";

/**
 * Owns the custom-placeholder input state for the create-from-template save screen.
 * Deliberately separate from useMetaDataFormInputs' inputValuesToSave: custom values are
 * placeholder-only and must never reach the SharePoint metadata paths.
 */
// Whitespace-only counts as empty: the fill engines trim a replacement's outer whitespace,
// so "   " would satisfy "required" here and then render as nothing in the document.
const isEmptyValue = (value: any): boolean =>
  value === undefined || value === null
  || (typeof value === "string" && !value.trim())
  || (Array.isArray(value) && value.length === 0);

export function useCustomFieldInputs(definitions?: ICustomFieldDefinition[]) {
  const [customInputValues, setCustomInputValues] = React.useState<SharePointInputData[]>([]);

  React.useEffect(() => {
    setCustomInputValues((definitions ?? []).map((def, index) => customFieldToInputData(def, index)));
  }, [definitions]);

  const handleCustomInputChange = React.useCallback((index: number, value: any, hasError: boolean) => {
    setCustomInputValues(previous => previous.map(input => input.index === index
      ? { ...input, value, hasError: hasError || isEmptyValue(value) }
      : input));
  }, []);

  // Returns the display names of invalid inputs (empty array = all valid), so the caller
  // can merge them into the same "Validation failed for" toast the SharePoint fields use -
  // custom inputs live outside inputValuesToSave, so that toast cannot see them itself.
  const validateCustomInputs = React.useCallback((requiredMessage?: string): string[] => {
    // Compute from the closure value, NOT inside a setState updater: React 18 does not run
    // updater functions synchronously, so a list built inside one is stale at return time.
    const invalidNames: string[] = [];
    const next = customInputValues.map(input => {
      const empty = isEmptyValue(input.value);
      if (empty || input.hasError) invalidNames.push(input.displayName);
      // Without an errorMessage the input renders no visible error (InputMetaDataFactory maps
      // hasError with an empty message to nothing) - the Create click would look dead.
      return empty ? { ...input, hasError: true, errorMessage: requiredMessage } : input;
    });
    setCustomInputValues(next);
    return invalidNames;
  }, [customInputValues]);

  const buildCustomPlaceholders = React.useCallback((regionalSetting?: { DecimalSeparator?: string; ThousandSeparator?: string }): CustomPlaceholderValue[] => {
    const defs = definitions ?? [];
    return customInputValues.map(input => {
      const def = defs[input.index];
      return { name: def.name, type: def.type, value: serializeCustomFieldValue(def, input.value, regionalSetting) };
    });
  }, [customInputValues, definitions]);

  return { customInputValues, handleCustomInputChange, validateCustomInputs, buildCustomPlaceholders };
}
